/**
 * Deep Link Handler for Capacitor
 * Handles payment callbacks from Stone and other payment apps
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/utils/logger';

// Conditional import for Capacitor App plugin
let CapacitorApp: typeof import('@capacitor/app').App | null = null;

// Try to load Capacitor only in native environment
const loadCapacitor = async () => {
  try {
    const module = await import('@capacitor/app');
    CapacitorApp = module.App;
    return true;
  } catch {
    // Not running in Capacitor environment
    return false;
  }
};

/**
 * Hook to handle Deep Links from payment apps
 * Routes: gonx://payment-callback, gonx://callback
 */
export function useDeepLinkHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const setupDeepLinkListener = async () => {
      const isCapacitor = await loadCapacitor();
      
      if (!isCapacitor || !CapacitorApp) {
        logger.log('[DeepLink] Not running in Capacitor environment');
        return;
      }

      logger.log('[DeepLink] Setting up Deep Link listener');

      // Listener para Deep Links quando o app está aberto
      const listener = await CapacitorApp.addListener('appUrlOpen', ({ url }) => {
        logger.log('[DeepLink] Received URL:', url);
        
        try {
          const urlObj = new URL(url);
          
          // Handle payment callbacks
          // gonx://payment-callback?status=success&transaction_id=xxx
          // gonx://callback?...
          if (urlObj.host === 'payment-callback' || urlObj.host === 'callback') {
            logger.log('[DeepLink] Navigating to payment callback with params:', urlObj.search);
            navigate(`/payment-callback${urlObj.search}`);
          }
        } catch (error) {
          logger.error('[DeepLink] Error parsing URL:', error);
        }
      });

      cleanup = () => {
        listener.remove();
      };
    };

    setupDeepLinkListener();

    return () => {
      cleanup?.();
    };
  }, [navigate]);
}

/**
 * Check if running in Capacitor native environment
 */
export function isCapacitorNative(): boolean {
  return typeof (window as any)?.Capacitor !== 'undefined' && 
         (window as any)?.Capacitor?.isNativePlatform?.() === true;
}

/**
 * Get platform info
 */
export function getPlatformInfo(): { isNative: boolean; platform: string } {
  const capacitor = (window as any)?.Capacitor;
  
  if (!capacitor) {
    return { isNative: false, platform: 'web' };
  }
  
  return {
    isNative: capacitor.isNativePlatform?.() ?? false,
    platform: capacitor.getPlatform?.() ?? 'unknown'
  };
}
